// ABOUTME: Modal for printing a serial number barcode with pre-filled barcode, name input, amount input, and workspace selection.
// ABOUTME: Used from the inbound processing view when clicking the print icon on a serial number.

import { Box, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { tokens } from "../../../theme";
import SHModal from "../../theme/Modal/SHModal";
import Shbutton from "../../theme/buttons/Shbutton";
import Subheading from "../../theme/text/Subheading";
import Text from "../../theme/text/Text";
import ButtonGroup from "../../theme/buttons/ButtonGroup";
import ShValidatedInput from "../../theme/inputs/ShValidatedInput";
import ShValidatedInputDropdown from "../../theme/dropdowns/ShValidatedInputDropdown";
import { showToastMessagePostRequest, showToastMessageRequestError } from "../../../utils/toasts";
import { useFindWorkspacesSelectQuery, usePrintBarcodeMutation } from "../../../newapi/warehouse/workspaceSlice";

const MPrintSerialNumberBarcode = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [printBarcode, { isLoading }] = usePrintBarcodeMutation();
    const { data: workSpaceData, isLoading: workspacesLoading } = useFindWorkspacesSelectQuery();

    const selectedWorkSpace = useSelector((state) => state.settings.workSpace);

    const [formData, setFormData] = useState({
        barcode: '',
        name: '',
        amount: 1,
        selectedWorkspace: selectedWorkSpace?.id || -1,
    });

    const [fieldErrors, setFieldErrors] = useState({});
    const [workspaces, setWorkspaces] = useState([]);

    const validationSchema = yup.object().shape({
        amount: yup.number()
            .typeError(t('amount_must_be_number'))
            .required(t('amount_required'))
            .positive(t('amount_must_be_positive'))
            .integer(t('amount_must_be_integer')),
        name: yup.string().required(t('product_name_required')),
        selectedWorkspace: yup.number().min(0, t('workspace_required'))
    });

    // Initialize workspaces data
    useEffect(() => {
        if (!workspacesLoading && workSpaceData) {
            const modifiedArray = workSpaceData.data.map(item => ({
                ...item,
                title: item.name,
                value: item.id
            }));
            setWorkspaces(modifiedArray);
        }
    }, [workSpaceData, workspacesLoading]);

    // Initialize data when modal opens
    useEffect(() => {
        if (props.open) {
            setFormData({
                barcode: props.barcode || '',
                name: props.barcode || '',
                amount: 1,
                selectedWorkspace: selectedWorkSpace?.id || -1,
            });
            setFieldErrors({});
        }
    }, [props.open, props.barcode, selectedWorkSpace]);

    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        if (fieldErrors[field]) {
            setFieldErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const handleClose = () => {
        setFormData({
            barcode: '',
            name: '',
            amount: 1,
            selectedWorkspace: selectedWorkSpace?.id || -1,
        });
        setFieldErrors({});
        props.handleClose();
    };

    const handlePrint = async () => {
        try {
            if (isLoading) return;

            await validationSchema.validate(formData, { abortEarly: false });
            setFieldErrors({});

            const printData = {
                amount: parseInt(formData.amount),
                barcode: formData.barcode,
                productId: props.productId,
                productName: formData.name,
                workspaceId: formData.selectedWorkspace,
            };

            const response = await printBarcode(printData).unwrap();
            showToastMessagePostRequest(t, response);
            handleClose();
        } catch (err) {
            if (err.name === 'ValidationError') {
                const errors = err.inner.reduce((acc, currentError) => ({
                    ...acc,
                    [currentError.path]: currentError.message
                }), {});
                setFieldErrors(errors);
            } else {
                showToastMessageRequestError(t('print_failed'), t, err);
            }
        }
    };

    let isStandAlone = false;
    if (window.matchMedia('(display-mode: standalone)').matches) {
        isStandAlone = true;
    }

    return (
        <SHModal
            open={props.open}
            onClose={handleClose}
        >
            <Box
                sx={{
                    textAlign: "center",
                    paddingY: 4,
                    pb:3,
                    paddingX: 5,
                    display: "flex",
                    flexDirection: "column",
                    alignItems:'center',
                }}
            >
                <Subheading>{t("print_barcode")}</Subheading>
                <Text light>{props.barcode}</Text>
            </Box>

            <Box
                sx={{
                    background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                    padding: 3,
                    pb:1,
                    borderRadius: 4,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5
                }}
            >
                {/* Name */}
                <Box
                    sx={{
                        background: theme.palette.mode === "dark" ? colors.green[100] : colors.green[100],
                        display:"flex",
                        alignItems:'center',
                        borderRadius:4,
                        overflow:'hidden',
                        px:0.5,
                    }}
                >
                    {/* <Text semiBold sx={{ mb: 1 }}>{t("name")}</Text> */}

                    <Box
                        sx={{
                            background: theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[0],
                            height:24,
                            borderRadius:3,
                            minWidth:80,
                            textAlign:"center"
                        }}
                    >

                        <Text color={theme.palette.mode === "dark" ? colors.txt["primary"] : colors.txt["primary"]} >{t("name")}</Text>
                    </Box>
                    <ShValidatedInput
                        name="name"
                        value={formData.name}
                        onChange={(field, value) => handleChange('name', value)}
                        error={fieldErrors.name}
                    />
                </Box>

                {/* Amount */}
                <Box
                    sx={{
                        background: theme.palette.mode === "dark" ? colors.green[100] : colors.green[100],
                        display:"flex",
                        alignItems:'center',
                        borderRadius:4,
                        overflow:'hidden',
                        px:0.5,
                    }}
                >
                    
                    <Box
                        sx={{
                            background: theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[0],
                            height:24,
                            borderRadius:3,
                            minWidth:80,
                            textAlign:"center"
                        }}
                    >

                        <Text color={theme.palette.mode === "dark" ? colors.txt["primary"] : colors.txt["primary"]} >{t("amount")}</Text>
                    </Box>
                    <ShValidatedInput
                        name="amount"
                        value={formData.amount}
                        onChange={(field, value) => {
                            const parsed = parseInt(value);
                            if (value === '' || (!isNaN(parsed) && parsed > 0)) {
                                handleChange('amount', value === '' ? '' : parsed);
                            }
                        }}
                        error={fieldErrors.amount}
                    />
                </Box>

                {/* Workspace Selection */}
                <Box
                    sx={{
                        background: theme.palette.mode === "dark" ? colors.green[100] : colors.green[100],
                        display:"flex",
                        alignItems:'center',
                        borderRadius:4,
                        overflow:'hidden',
                        px:0.5,
                    }}
                >

                    <Box
                        sx={{
                            background: theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[0],
                            height:24,
                            borderRadius:3,
                            minWidth:80,
                            textAlign:"center"
                        }}
                    >

                        <Text color={theme.palette.mode === "dark" ? colors.txt["primary"] : colors.txt["primary"]} >{t("workspace")}</Text>
                    </Box>
                    <ShValidatedInputDropdown
                        name="workspace"
                        displayName="title"
                        changeField="value"
                        selected={workspaces.findIndex(ws => ws.id === formData.selectedWorkspace)}
                        options={workspaces}
                        noSelection={t("select_workspace")}
                        onChange={(field, value) => handleChange('selectedWorkspace', value)}
                        error={fieldErrors.selectedWorkspace}
                    />
                </Box>
            </Box>


            <Box
                component="footer"
                sx={{
                    
                    display:"block",
                    width:1,
                    paddingBottom: isStandAlone ? "32px" : "16px",
                    display:"flex",
                    alignItems:"center",
                    paddingRight:3,
                    zIndex:1058,
                    position:"relative",
                    paddingTop:2,
                    background:`${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                    borderBottomLeftRadius:24,
                    borderBottomRightRadius:24,
                    justifyContent:"space-between",
                    "@media screen and (max-width: 47.9375em)" : {
                        paddingRight:0,
                    }
                }}
            >
                <Shbutton isCancel={true} className={"always-visible"} onClick={handleClose} color="primary" variant="text" startIcon={<ArrowRightAltIcon/>}>{t("cancel")}</Shbutton>
                <ButtonGroup version={2}>
                    <Shbutton
                        className={"group-btn"}
                        onClick={handlePrint}
                        color={theme.palette.mode === "dark" ? "blue" : "primary"}
                        variant="contained"
                        isLoading={isLoading}
                        disabled={isLoading || formData.selectedWorkspace === -1}
                    >
                        {isLoading ? t("printing") : t("print")}
                    </Shbutton>
                </ButtonGroup>
            </Box>
        </SHModal>
    );
};

export default MPrintSerialNumberBarcode;
